import { ApolloError } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";

import {
  DiscoveryProfileIdsQueryVariables,
  useDiscoveryProfileIdsQuery,
} from "../../../graphql/discoveryProfileIds.generated";
import {
  GetDiscoveryProfilesQuery,
  useGetDiscoveryProfilesQuery,
} from "../../../graphql/getDiscoveryProfiles.generated";

// HackerAPI caps pipeline-scoped `claims` results at 50 for non-organizers.
// Keep this at or below 50 unless that backend contract changes.
export const BATCH_SIZE = 20;
const DISCOVERY_PROFILES_PIPELINE_SLUG = `${CURRENT_EVENT_SLUG}-discovery-profiles`;

type DiscoveryProfile = GetDiscoveryProfilesQuery["profiles"][number];

const indexProfilesById = (
  profiles: DiscoveryProfile[]
): Record<number, DiscoveryProfile> =>
  profiles.reduce<Record<number, DiscoveryProfile>>(
    (indexedProfiles, profile) => {
      indexedProfiles[profile.id] = profile;
      return indexedProfiles;
    },
    {}
  );

interface UseDiscoveryProfilesResult {
  profiles: Array<DiscoveryProfile | undefined>;
  rateLimited: boolean;
  initial: {
    loading: boolean;
    error: ApolloError | null;
  };
  batch: {
    loading: boolean;
    error: ApolloError | null;
    nextSize: number;
    hasMore: boolean;
  };
  retryInitial: () => void;
  loadNextBatch: () => void;
}

interface UseDiscoveryProfilesOptions {
  skipProfileIdsQuery?: boolean;
  cachedProfileIds?: number[] | null;
  onProfileIdsLoaded?: (profileIds: number[]) => void;
}

const isRateLimitedError = (error: ApolloError | null): boolean =>
  error?.networkError != null &&
  "statusCode" in error.networkError &&
  error.networkError.statusCode === 429;

const useDiscoveryProfiles = (
  profileIdsVariables: DiscoveryProfileIdsQueryVariables,
  {
    skipProfileIdsQuery = false,
    cachedProfileIds = null,
    onProfileIdsLoaded,
  }: UseDiscoveryProfilesOptions = {}
): UseDiscoveryProfilesResult => {
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState<ApolloError | null>(null);
  const batchRequestPendingRef = useRef(false);
  const isMountedRef = useRef(true);

  // A valid Auto-Suggest cache skips only the semantic ID lookup. The profile
  // query below remains network-only so every cached ID is freshly hydrated.
  const profileIdsQuery = useDiscoveryProfileIdsQuery({
    variables: profileIdsVariables,
    skip: skipProfileIdsQuery || cachedProfileIds !== null,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const fetchedProfileIds = useMemo(
    () =>
      profileIdsQuery.loading || profileIdsQuery.error
        ? []
        : Array.from(new Set(profileIdsQuery.data?.discoveryProfileIds ?? [])),
    [profileIdsQuery.data, profileIdsQuery.error, profileIdsQuery.loading]
  );
  const profileIds = cachedProfileIds ?? fetchedProfileIds;
  const profileIdsLoading =
    cachedProfileIds === null && profileIdsQuery.loading;
  const profileIdsError =
    cachedProfileIds === null ? profileIdsQuery.error : undefined;

  useEffect(() => {
    if (
      !onProfileIdsLoaded ||
      cachedProfileIds !== null ||
      skipProfileIdsQuery ||
      profileIdsQuery.loading ||
      profileIdsQuery.error ||
      !profileIdsQuery.data
    )
      return;

    onProfileIdsLoaded(fetchedProfileIds);
  }, [
    cachedProfileIds,
    fetchedProfileIds,
    onProfileIdsLoaded,
    profileIdsQuery.data,
    profileIdsQuery.error,
    profileIdsQuery.loading,
    skipProfileIdsQuery,
  ]);

  const initialProfileIds = useMemo(
    () => profileIds.slice(0, BATCH_SIZE),
    [profileIds]
  );

  const profilesQuery = useGetDiscoveryProfilesQuery({
    variables: {
      profileIds: initialProfileIds,
      pipelineSlug: DISCOVERY_PROFILES_PIPELINE_SLUG,
    },
    skip:
      profileIdsLoading || !!profileIdsError || initialProfileIds.length === 0,
    fetchPolicy: "network-only",
    // Apollo 3.3 re-observes the base query after fetchMore. Read the merged
    // result from the cache instead of requesting and restoring the first batch.
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
  });

  const profilesById = useMemo(
    () => indexProfilesById(profilesQuery.data?.profiles ?? []),
    [profilesQuery.data]
  );
  const profiles = profileIds
    .slice(0, visibleCount)
    .map((profileId) => profilesById[profileId]);
  const initialProfilesLoading =
    initialProfileIds.length > 0 &&
    !profilesQuery.data &&
    profilesQuery.loading;
  const initialError =
    profileIdsError ??
    (!profilesQuery.data ? profilesQuery.error : undefined) ??
    null;
  const initialLoading =
    (profileIdsLoading || initialProfilesLoading) && !initialError;
  const hasMore = visibleCount < profileIds.length;

  const retryInitial = useCallback(() => {
    if (profileIdsError) {
      void profileIdsQuery.refetch();
      return;
    }

    void profilesQuery.refetch();
  }, [profileIdsError, profileIdsQuery, profilesQuery]);

  const loadNextBatch = useCallback(() => {
    if (
      initialLoading ||
      initialError ||
      batchRequestPendingRef.current ||
      !hasMore
    )
      return;

    const nextVisibleCount = Math.min(
      visibleCount + BATCH_SIZE,
      profileIds.length
    );
    const nextProfileIds = profileIds.slice(visibleCount, nextVisibleCount);

    batchRequestPendingRef.current = true;
    setBatchLoading(true);
    setBatchError(null);

    profilesQuery
      .fetchMore({
        variables: {
          profileIds: nextProfileIds,
          pipelineSlug: DISCOVERY_PROFILES_PIPELINE_SLUG,
        },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previous;

          const existingProfileIds = new Set(
            previous.profiles.map((profile) => profile.id)
          );
          return {
            profiles: [
              ...previous.profiles,
              ...fetchMoreResult.profiles.filter(
                (profile) => !existingProfileIds.has(profile.id)
              ),
            ],
          };
        },
      })
      .then(() => {
        if (isMountedRef.current) setVisibleCount(nextVisibleCount);
      })
      .catch((error: ApolloError) => {
        if (isMountedRef.current) setBatchError(error);
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        batchRequestPendingRef.current = false;
        setBatchLoading(false);
      });
  }, [
    hasMore,
    initialError,
    initialLoading,
    profileIds,
    profilesQuery,
    visibleCount,
  ]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  return {
    profiles,
    rateLimited:
      isRateLimitedError(initialError) || isRateLimitedError(batchError),
    initial: {
      loading: initialLoading,
      error: initialError,
    },
    batch: {
      loading: batchLoading,
      error: batchError,
      nextSize: Math.min(
        BATCH_SIZE,
        Math.max(0, profileIds.length - visibleCount)
      ),
      hasMore,
    },
    retryInitial,
    loadNextBatch,
  };
};

export default useDiscoveryProfiles;
